import { CircleHelp } from 'lucide-react';
import { cn } from '@/lib/utils';

const HELP_AUDIENCES = ['admin', 'account_manager', 'recruiter', 'viewer', 'client'] as const;

interface HelpLinkProps {
  audience: string;
  variant?: 'header' | 'menu';
  className?: string;
  onClick?: () => void;
}

function HelpShine() {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 -skew-x-12',
        'bg-gradient-to-r from-transparent via-red-300/60 to-transparent opacity-75',
        'transition-transform duration-700 ease-out group-hover:translate-x-[500%]',
        'motion-reduce:hidden',
      )}
    />
  );
}

export function HelpLink({
  audience,
  variant = 'header',
  className,
  onClick,
}: HelpLinkProps) {
  const normalizedAudience = HELP_AUDIENCES.some((role) => role === audience)
    ? audience
    : 'all';
  const href = `/help/crm-guide.html?role=${encodeURIComponent(normalizedAudience)}`;

  if (variant === 'menu') {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={cn(
          'group relative isolate mx-2 my-1 flex w-[calc(100%-1rem)] items-center gap-2',
          'overflow-hidden rounded-lg border border-emerald-400/65 bg-red-950/75 px-3 py-2 text-sm',
          'font-semibold text-emerald-300 shadow-[0_0_16px_rgba(239,68,68,0.24)]',
          'transition-all duration-300 hover:border-emerald-300 hover:bg-red-900/85',
          'hover:text-emerald-200 hover:shadow-[0_0_22px_rgba(239,68,68,0.42)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/70',
          className,
        )}
      >
        <HelpShine />
        <CircleHelp className="relative z-10 h-4 w-4 drop-shadow-[0_0_5px_rgba(248,113,113,0.75)]" />
        <span className="relative z-10">Help guide</span>
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      aria-label="Open the Help guide"
      title="Open Help guide"
      className={cn(
        'group relative isolate inline-flex h-10 items-center justify-center gap-2 overflow-hidden',
        'rounded-lg border border-emerald-400/70 bg-gradient-to-br from-red-950 via-red-950 to-red-900/80',
        'px-3 text-sm font-semibold tracking-wide text-emerald-300',
        'shadow-[0_0_0_1px_rgba(248,113,113,0.10),0_0_18px_rgba(239,68,68,0.30),inset_0_1px_0_rgba(255,255,255,0.08)]',
        'transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-300',
        'hover:text-emerald-200 hover:shadow-[0_0_0_1px_rgba(110,231,183,0.18),0_0_26px_rgba(239,68,68,0.48),inset_0_1px_0_rgba(255,255,255,0.12)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/80',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:hover:translate-y-0',
        className,
      )}
    >
      <HelpShine />
      <CircleHelp className="relative z-10 h-4 w-4 drop-shadow-[0_0_6px_rgba(248,113,113,0.85)]" />
      <span className="relative z-10 hidden xl:inline">Help</span>
    </a>
  );
}
